/**
 * AutoQuoteExistingClientPanel — Section 5 (iter-130).
 * One-tap auto-quote generator for existing NECS clients. Meant to be
 * mounted inside the Customer Service and Sales command centers. During
 * business hours the quote email is sent immediately; outside hours it
 * queues and drains at the next business-day start via admin action or
 * scheduler.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Send, Zap, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AutoQuoteExistingClientPanel({ testId = 'auto-quote-panel' }) {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState([]);

  const loadQueue = useCallback(async () => {
    const r = await fetch(`${API}/api/auto-quote/queue`, { credentials: 'include' });
    if (r.ok) setQueue(await r.json());
  }, []);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Client email required');
    setBusy(true);
    const r = await fetch(`${API}/api/auto-quote/existing`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), note }),
    });
    setBusy(false);
    if (r.ok) {
      const j = await r.json();
      toast.success(j.status === 'sent'
        ? `Quote sent to ${email}`
        : `Queued for next business day (${j.status})`);
      setEmail(''); setNote(''); loadQueue();
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Auto-quote failed');
    }
  };

  const drain = async () => {
    const r = await fetch(`${API}/api/auto-quote/drain-queue`, { method: 'POST', credentials: 'include' });
    if (r.ok) {
      const j = await r.json();
      toast.success(`Drained ${j.drained} quote(s)`);
      loadQueue();
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,214,10,0.15)', border: '1px solid rgba(255,214,10,0.4)' }}>
            <Zap className="h-5 w-5" style={{ color: '#FFD60A' }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Auto-Quote · Existing Client
            </h3>
            <p className="text-[11px] text-zinc-500">
              Pulls from active contract + property records. Business hours send instantly; off-hours queue.
            </p>
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={drain} data-testid="auto-quote-drain-btn">
          <Clock className="h-3 w-3 mr-1" /> Drain queue
        </Button>
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-xs text-zinc-400">Client email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="pm@property.com"
                 className="bg-zinc-800 border-zinc-700 text-white" data-testid="auto-quote-email" />
        </div>
        <div className="sm:col-span-1">
          <Label className="text-xs text-zinc-400">Note (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Renewal · new site"
                 className="bg-zinc-800 border-zinc-700 text-white" data-testid="auto-quote-note" />
        </div>
        <Button type="submit" disabled={busy} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="auto-quote-submit">
          <Send className="h-4 w-4 mr-2" /> {busy ? 'Generating…' : 'Send Auto-Quote'}
        </Button>
      </form>

      {queue.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Recent auto-quotes</p>
          <ul className="space-y-1 text-xs">
            {queue.slice(0, 8).map((q) => (
              <li key={q.quote_id} className="flex justify-between text-zinc-300 border-b border-zinc-800 py-1">
                <span>{q.client_email} · {q.property_name || '—'} · {q.package_name}</span>
                <Badge className={q.status === 'sent' ? 'bg-emerald-700 text-white' : 'bg-amber-600 text-black'}>
                  {q.status}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
